import type { Agent } from './agent';
import type {
  CompactBoundaryRecord,
  CompactSource,
  CompactSummaryRecord,
  CompactTrigger,
} from './compaction/types';
import { type RunItem } from './items';
import type { ResponseStreamEvent } from './types';

/**
 * The names of the events that can be generated by the agent.
 */
export type RunItemStreamEventName =
  | 'message_output_created'
  | 'handoff_requested'
  | 'handoff_occurred'
  | 'tool_search_called'
  | 'tool_search_output_created'
  | 'tool_called'
  | 'tool_output'
  | 'reasoning_item_created'
  | 'tool_approval_requested'
  | 'compact_boundary_created'
  | 'compact_summary_created';

function getRawModelEventSource(data: ResponseStreamEvent): string | undefined {
  if (data.type !== 'model') {
    return undefined;
  }

  const source = data.providerData?.rawModelEventSource;
  return typeof source === 'string' ? source : undefined;
}

/**
 * Streaming event from the LLM. These are `raw` events, i.e. they are directly passed through from
 * the LLM.
 */
export class RunRawModelStreamEvent {
  /**
   * The type of the event.
   */
  public readonly type = 'raw_model_stream_event';

  /**
   * @param data The raw responses stream events from the LLM.
   */
  constructor(
    public data: ResponseStreamEvent,
    public readonly source: string | undefined = getRawModelEventSource(data),
  ) {}
}

/**
 * Streaming events that wrap a `RunItem`. As the agent processes the LLM response, it will generate
 * these events from new messages, tool calls, tool outputs, handoffs, etc.
 */
export class RunItemStreamEvent {
  public readonly type = 'run_item_stream_event';

  /**
   * @param name The name of the event.
   * @param item The item that was created.
   */
  constructor(
    public name: RunItemStreamEventName,
    public item: RunItem,
  ) {}
}

/**
 * Event that notifies that there is a new agent running.
 */
export class RunAgentUpdatedStreamEvent {
  public readonly type = 'agent_updated_stream_event';

  /**
   * @param agent The new agent
   */
  constructor(public agent: Agent<any, any>) {}
}

export type RunCompactionStreamEventName =
  | 'compact_started'
  | 'compact_completed'
  | 'compact_failed';

export type RunCompactionStreamEventData = {
  id: string;
  trigger: CompactTrigger;
  source?: CompactSource;
  reason?: 'threshold' | 'manual' | 'context_length_exceeded';
  thresholdTokens?: number;
  preCompactTokenCount?: number;
  postCompactTokenCount?: number;
  summarizedItemCount?: number;
  preservedItemCount?: number;
  boundaryItem?: CompactBoundaryRecord;
  summaryItem?: CompactSummaryRecord;
  error?: string;
};

export class RunCompactionStreamEvent {
  public readonly type = 'compaction_stream_event';

  constructor(
    public name: RunCompactionStreamEventName,
    public data: RunCompactionStreamEventData,
  ) {}
}

/**
 * A streaming event from an agent run.
 */
export type RunStreamEvent =
  | RunRawModelStreamEvent
  | RunItemStreamEvent
  | RunAgentUpdatedStreamEvent
  | RunCompactionStreamEvent;
